import { useGetShoppingList } from "@/hooks/requests/shopping-list/use-get-shopping-list";
import { useUpdateShoppingList } from "@/hooks/requests/shopping-list/use-update-shopping-list";
import { useToast } from "@/hooks/ui/use-toast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { groupIngredientsByAisle } from "../../utils/shopping-list-helpers";

/**
 * A variant of useShopList that manages the selected aisle via local React state
 * instead of URL search params — suitable for use inside a modal.
 */
export const useShopListModal = () => {
  const { ingredients, isLoading, error, refetchShoppingList } = useGetShoppingList();
  const { updateShoppingList, isUpdating } = useUpdateShoppingList();
  const { showSuccess, showError } = useToast();

  const [groupedIngredients, ingredientCategories] = useMemo(() => {
    const grouped = groupIngredientsByAisle(ingredients ?? []);
    const categories = Array.from(grouped.keys());
    return [grouped, categories];
  }, [ingredients]);

  const [selectedAisle, setSelectedAisle] = useState<string | null>(null);

  /** Auto-select the first aisle that has items whenever data loads */
  useEffect(() => {
    if (selectedAisle) return;
    if (groupedIngredients.size > 0) {
      const firstAisleWithItems = Array.from(groupedIngredients.keys()).find(
        (aisle) => (groupedIngredients.get(aisle)?.length ?? 0) > 0,
      );
      if (firstAisleWithItems) {
        setSelectedAisle(firstAisleWithItems);
      }
    }
  }, [selectedAisle, groupedIngredients]);

  const selectedIngredients = groupedIngredients.get(selectedAisle ?? "") ?? [];

  const tickedItems = useMemo(() => {
    return new Set(
      ingredients
        ?.filter((ingredient) => ingredient.isTicked)
        .map((ingredient) => ingredient.ingredientId) ?? [],
    );
  }, [ingredients]);

  const handleToggleItem = useCallback(
    (ingredientId: string) => {
      return () => {
        const newTickedItems = new Set(tickedItems);

        if (newTickedItems.has(ingredientId)) {
          newTickedItems.delete(ingredientId);
        } else {
          newTickedItems.add(ingredientId);
        }

        updateShoppingList(
          { tickedIngredientIds: Array.from(newTickedItems) },
          {
            onError: (err) => {
              console.error("Failed to update shopping list:", err);
              showError("Failed to update shopping list.");
            },
          },
        );
      };
    },
    [tickedItems, updateShoppingList, showError],
  );

  const handleMarkAllAsBought = useCallback(() => {
    const allIds = ingredients?.map((ing) => ing.ingredientId) ?? [];

    updateShoppingList(
      { tickedIngredientIds: allIds },
      {
        onSuccess: () => showSuccess("All items marked as bought!"),
        onError: (err) => {
          console.error("Failed to mark all as bought:", err);
          showError("Failed to mark all items as bought.");
        },
      },
    );
  }, [ingredients, updateShoppingList, showSuccess, showError]);

  const handleResetList = useCallback(() => {
    updateShoppingList(
      { tickedIngredientIds: [] },
      {
        onSuccess: () => showSuccess("Shopping list reset!"),
        onError: (err) => {
          console.error("Failed to reset list:", err);
          showError("Failed to reset shopping list.");
        },
      },
    );
  }, [updateShoppingList, showSuccess, showError]);

  return {
    ingredients,
    isLoading,
    error,
    refetchShoppingList,
    selectedAisle,
    setSelectedAisle,
    tickedItems,
    groupedIngredients,
    ingredientCategories,
    selectedIngredients,
    handleToggleItem,
    handleMarkAllAsBought,
    handleResetList,
    isUpdating,
  };
};
